import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import { Badge } from "../../../ui/badge";
import { Plus, Trash2, Edit2 } from "lucide-react";
import { toast } from "sonner";
import useRetryFetch from "../../../../hooks/useRetryFetch";
import {
  listCategories,
  createCategory as createCategoryAPI,
  deleteCategory as deleteCategoryAPI,
  UserCategory,
} from "../../../../api/userCategories";

interface CategoryManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: "action" | "insight" | "both";
  onCategoryCreated?: (category: UserCategory) => void;
  onCategoryDeleted?: (categoryId: string) => void;
}

const CategoryManagementDialog: React.FC<CategoryManagementDialogProps> = ({
  open,
  onOpenChange,
  type,
  onCategoryCreated,
  onCategoryDeleted,
}) => {
  const { t } = useTranslation();
  const retryFetch = useRetryFetch();
  const [categories, setCategories] = useState<UserCategory[]>([]);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryColor, setNewCategoryColor] = useState("#3b82f6");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchCategories();
    }
  }, [open]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await listCategories(retryFetch, type);
      setCategories(response.list);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
      toast.error(t("failed_to_load_categories"));
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const createCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error(t("category_name_required"));
      return;
    }

    try {
      const response = await createCategoryAPI(retryFetch, {
        name: newCategoryName.trim(),
        color: newCategoryColor,
        type: "both", // Always create categories for both action items and insights
      });

      if (response.status === "CREATED" && response.category) {
        setCategories([...categories, response.category]);
        setNewCategoryName("");
        setNewCategoryColor("#3b82f6");
        toast.success(t("category_created"));
        onCategoryCreated?.(response.category);
      } else if (response.status === "ERROR") {
        if (response.fault === "duplicate_category_name") {
          toast.error(t("category_name_exists"));
        } else {
          toast.error(t("failed_to_create_category"));
        }
      }
    } catch (error) {
      console.error("Failed to create category:", error);
      toast.error(t("failed_to_create_category"));
    }
  };

  const deleteCategory = async (categoryId: string) => {
    try {
      const response = await deleteCategoryAPI(retryFetch, { id: categoryId });

      if (response.status === "DELETED") {
        setCategories(categories.filter(c => c._id !== categoryId));
        toast.success(t("category_deleted"));
        onCategoryDeleted?.(categoryId);
      }
    } catch (error) {
      console.error("Failed to delete category:", error);
      toast.error(t("failed_to_delete_category"));
    }
  };

  const filteredCategories = categories.filter(
    c => c.type === type || c.type === "both"
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("manage_categories")}</DialogTitle>
          <DialogDescription>
            {t("manage_categories_description")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Create New Category */}
          <div className="flex gap-2">
            <div className="flex-1 space-y-2">
              <Label htmlFor="category-name">{t("category_name")}</Label>
              <Input
                id="category-name"
                placeholder={t("enter_category_name")}
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    createCategory();
                  }
                }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category-color">{t("color")}</Label>
              <Input
                id="category-color"
                type="color"
                value={newCategoryColor}
                onChange={(e) => setNewCategoryColor(e.target.value)}
                className="w-20 h-10"
              />
            </div>
            <div className="flex items-end">
              <Button onClick={createCategory}>
                <Plus className="h-4 w-4 mr-2" />
                {t("add")}
              </Button>
            </div>
          </div>

          {/* Categories List */}
          <div className="border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("category_name")}</TableHead>
                  <TableHead>{t("color")}</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center">
                      {t("loading")}...
                    </TableCell>
                  </TableRow>
                ) : filteredCategories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground">
                      {t("no_categories_yet")}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCategories.map((category) => (
                    <TableRow key={category._id}>
                      <TableCell className="font-medium">
                        {category.name}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-6 h-6 rounded border"
                            style={{ backgroundColor: category.color }}
                          />
                          <span className="text-sm text-muted-foreground">
                            {category.color}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteCategory(category._id)}
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CategoryManagementDialog;
